/**
 * Sign-out for the web apps (Jean, 30 Oct 2026): signing out ends the session
 * on the identity server and lands on the app's own signed-out page, which
 * never redirects to sign-in by itself. "Sign in again" asks for credentials.
 *
 * Before this, each app cleared its user, re-rendered its auto-redirecting
 * sign-in screen, and that screen's login redirect replaced the logout one
 * before the page unloaded: the identity session survived and signed the
 * visitor straight back in.
 */
import { resetRedirects } from './signInRedirect';

export const SIGNED_OUT_PATH = '/signed-out';

let signingOut = false;

export function isSignedOutPath(pathname) {
  return /^\/signed-out\/?$/.test(String(pathname || ''));
}

/** Where the identity server sends the browser after logout (post_logout_redirect_uri). */
export function signedOutUrl(origin = window.location.origin) {
  return `${String(origin || '').replace(/\/+$/, '')}${SIGNED_OUT_PATH}`;
}

/** Where "Sign in again" returns after the credential step. */
export function signInAgainUrl(origin = window.location.origin) {
  return `${String(origin || '').replace(/\/+$/, '')}/`;
}

/** Options for keycloak.login() from the signed-out page: always ask for credentials. */
export function signInAgainOptions(origin) {
  return { redirectUri: signInAgainUrl(origin), prompt: 'login' };
}

/** True from the moment sign-out starts until the page unloads: no sign-in redirect may start meanwhile. */
export function isSigningOut() {
  return signingOut;
}

/**
 * Starts sign-out in the page: blocks automatic sign-in redirects and moves the
 * router to the signed-out page, so the app renders it (not its sign-in screen)
 * while the browser goes to the identity server's logout endpoint.
 */
export function beginSignOut(win = typeof window !== 'undefined' ? window : null) {
  signingOut = true;
  resetRedirects();
  if (!win) return;
  try {
    if (!isSignedOutPath(win.location.pathname)) {
      win.history.replaceState(null, '', SIGNED_OUT_PATH);
      // BrowserRouter follows popstate; replaceState alone does not notify it.
      win.dispatchEvent(new win.PopStateEvent('popstate', { state: null }));
    }
  } catch (_) { /* the logout redirect still runs */ }
}

/** Test hook. */
export function resetSignOutState() {
  signingOut = false;
}
